import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  schemaData?: Record<string, unknown>;
}

export default function SEO({ title, description, keywords, canonicalUrl, ogImage, ogType = "website", schemaData }: SEOProps) {
  const location = useLocation();
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const currentUrl = canonicalUrl || `${baseUrl}${location.pathname}`;

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("keywords", keywords);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:type", ogType, true);
    setMeta("og:url", currentUrl, true);
    setMeta("og:locale", "zh_CN", true);
    setMeta("og:site_name", "久久视频", true);
    if (ogImage) {
      setMeta("og:image", ogImage, true);
    }
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    if (ogImage) {
      setMeta("twitter:image", ogImage);
    }

    const linkEl = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (linkEl) {
      linkEl.href = currentUrl;
    } else {
      const newLink = document.createElement("link");
      newLink.rel = "canonical";
      newLink.href = currentUrl;
      document.head.appendChild(newLink);
    }
  }, [title, description, keywords, currentUrl, ogImage, ogType]);

  useEffect(() => {
    if (!schemaData) return;
    const existingScript = document.querySelector('script[type="application/ld+json"][data-schema="dynamic"]');
    if (existingScript) {
      existingScript.remove();
    }
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-schema", "dynamic");
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      const s = document.querySelector('script[type="application/ld+json"][data-schema="dynamic"]');
      if (s) s.remove();
    };
  }, [schemaData]);

  return null;
}